// src/admin/AdminEditModelPage.tsx
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { auth, db, storage } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import type { ModelDoc, ModelImage, Measurements } from "../types/model";
import "../styles/AdminEditModelPage.scss";


const emptyModel: ModelDoc = {
  name: "",
  race: "",
  category: "Fashion",
  instagram: "",
  heroUrl: "",
  images: [],
  measurements: {
    height: "", bust: "", waist: "", hips: "", shoe: "", eyes: "", hair: "",
  },
  active: true,
};

function uid() {
  return crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
}

const AdminEditModelPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [authReady, setAuthReady] = useState(false);
  const [user, setUser] = useState<any>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<number>(0);

  const [model, setModel] = useState<ModelDoc>(emptyModel);
  // Track original images shape so we can save back compatibly
  const [imagesWereStrings, setImagesWereStrings] = useState(false);

  // ── Auth gate ─────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  // ── Load model (normalize legacy fields) ──────────────────
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "models", id));
        if (!snap.exists()) {
          alert("Model not found.");
          navigate("/admin");
          return;
        }
        const raw = snap.data() as any;

        // Normalize images: allow both string[] and {url, caption?}[]
        const imgs = Array.isArray(raw.images) ? raw.images : [];
        const strings = imgs.length > 0 && typeof imgs[0] === "string";
        setImagesWereStrings(strings);

        const normalizedImages = strings
          ? (imgs as string[]).map((u) => ({ url: u }))
          : (imgs as Array<{ url: string; caption?: string }>);

        setModel({
          ...emptyModel,
          ...raw,
          id,
          // prefer `instagram`, fall back to legacy `instagramHandle`
          instagram: raw.instagram ?? raw.instagramHandle ?? "",
          images: normalizedImages,
        });
      } catch (e) {
        console.error(e);
        alert("Failed to load model.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  // ── Handlers: details/measurements ────────────────────────
  const setField = <K extends keyof ModelDoc>(key: K, value: ModelDoc[K]) =>
    setModel((m) => ({ ...m, [key]: value }));

  const setMeas = <K extends keyof Measurements>(key: K, value: Measurements[K]) =>
    setModel((m) => ({ ...m, measurements: { ...(m.measurements ?? {}), [key]: value } }));

  // ── Upload hero image ─────────────────────────────────────
  const heroInputRef = useRef<HTMLInputElement>(null);
  const onPickHero = () => heroInputRef.current?.click();

  const uploadHero = (file: File) => {
    if (!id) return;
    const storageRef = ref(storage, `models/${id}/hero_${file.name}`);
    const task = uploadBytesResumable(storageRef, file);
    task.on(
      "state_changed",
      (snap) => {
        const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
        setProgress(pct);
      },
      (err) => {
        console.error(err);
        alert("Hero upload failed");
      },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        setField("heroUrl", url);
        setProgress(0);
      }
    );
  };

  // ── Upload gallery images ─────────────────────────────────
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const onPickGallery = () => galleryInputRef.current?.click();

  const uploadGallery = (files: FileList) => {
    if (!id) return;
    Array.from(files).forEach((file) => {
      const storageRef = ref(storage, `models/${id}/${uid()}_${file.name}`);
      const task = uploadBytesResumable(storageRef, file);
      task.on(
        "state_changed",
        (snap) => {
          const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
          setProgress(pct);
        },
        (err) => {
          console.error(err);
          alert("Image upload failed");
        },
        async () => {
          const url = await getDownloadURL(task.snapshot.ref);
          setModel((m) => ({ ...m, images: [...(m.images ?? []), { url }] }));
          setProgress(0);
        }
      );
    });
  };

  // ── Delete a gallery image from Storage + state ───────────
  const removeImage = async (img: ModelImage) => {
    if (!id) return;
    try {
      // Simpler: delete by download URL (works for Firebase Storage URLs)
      await deleteObject(ref(storage, img.url)).catch(() => { /* ignore */ });
    } catch (_) {
      // ignore deletion failures (might be external URL)
    }
    setModel((m) => ({ ...m, images: m.images.filter((g) => g.url !== img.url) }));
  };

  // ── Reorder gallery (simple up/down) ──────────────────────
  const moveImage = (index: number, dir: -1 | 1) => {
    setModel((m) => {
      const arr = [...m.images];
      const target = index + dir;
      if (target < 0 || target >= arr.length) return m;
      [arr[index], arr[target]] = [arr[target], arr[index]];
      return { ...m, images: arr };
    });
  };

  // ── Save to Firestore (preserve original shapes) ──────────
  const onSave = async () => {
    if (!id) return;
    if (!model.name.trim()) {
      alert("Name is required.");
      return;
    }
    setSaving(true);
    try {
      const imagesForSave = imagesWereStrings
        ? (model.images ?? []).map((g) => g.url) // legacy: string[]
        : (model.images ?? []).map((g) => ({ url: g.url, caption: g.caption?.trim() || "" })); // new: objects

      const payload: any = {
        name: model.name.trim(),
        instagram: model.instagram?.trim() ?? "",
        // keep legacy field too for compatibility
        instagramHandle: model.instagram?.trim() ?? "",
        race: model.race ?? "",
        category: model.category ?? "",
        heroUrl: model.heroUrl ?? "",
        images: imagesForSave,
        measurements: {
          ...(model.measurements ?? {}),
          height: model.measurements?.height ?? "",
          bust: model.measurements?.bust ?? "",
          waist: model.measurements?.waist ?? "",
          hips: model.measurements?.hips ?? "",
          shoe: model.measurements?.shoe ?? "",
          eyes: model.measurements?.eyes ?? "",
          hair: model.measurements?.hair ?? "",
        },
        active: model.active ?? true,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(db, "models", id), payload);
      alert("Saved!");
    } catch (e) {
      console.error(e);
      alert("Failed to save changes.");
    } finally {
      setSaving(false);
    }
  };

  if (!authReady) return null;
  if (!user) return <div style={{ padding: 20 }}>Please sign in.</div>;
  if (loading) return <div style={{ padding: 20 }}>Loading model…</div>;

  return (
    <div className="admin-edit" style={{ maxWidth: 980, margin: "20px auto", padding: 16 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <h2 style={{ margin: 0 }}>Edit Model</h2>
        <div style={{ display: "inline-flex", gap: 8 }}>
          <button disabled={saving} onClick={() => navigate(-1)}>Back</button>
          <button disabled={saving} onClick={onSave}>{saving ? "Saving…" : "Save Changes"}</button>
        </div>
      </header>

      {progress > 0 && (
        <div style={{ margin: "12px 0" }}>
          <div style={{ height: 6, background: "#222", borderRadius: 6 }}>
            <div style={{ width: `${progress}%`, height: 6 }} />
          </div>
          <small>Uploading… {progress}%</small>
        </div>
      )}

      {/* Details */}
      <section style={{ marginTop: 18, padding: 12, border: "1px solid var(--stroke, #2a2f3a)", borderRadius: 12 }}>
        <h3>Details</h3>
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr" }}>
          <label>
            <div>Name</div>
            <input value={model.name} onChange={e => setField("name", e.target.value)} placeholder="Model name" />
          </label>
          <label>
            <div>Instagram</div>
            <input value={model.instagram ?? ""} onChange={e => setField("instagram", e.target.value)} placeholder="@handle or URL" />
          </label>
          <label>
            <div>Race</div>
            <input value={model.race ?? ""} onChange={e => setField("race", e.target.value)} placeholder="e.g., African" />
          </label>
          <label>
            <div>Category</div>
            <select value={model.category ?? "Fashion"} onChange={e => setField("category", e.target.value)}>
              <option>Fashion</option>
              <option>Commercial</option>
              <option>Runway</option>
              <option>Editorial</option>
              <option>Fitness</option>
            </select>
          </label>
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="checkbox" checked={!!model.active} onChange={e => setField("active", e.target.checked)} />
            Active
          </label>
        </div>
      </section>

      {/* Measurements */}
      <section style={{ marginTop: 18, padding: 12, border: "1px solid var(--stroke, #2a2f3a)", borderRadius: 12 }}>
        <h3>Measurements</h3>
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(4, 1fr)" }}>
          <label><div>Height</div><input value={model.measurements?.height ?? ""} onChange={e => setMeas("height", e.target.value)} placeholder="173 cm" /></label>
          <label><div>Bust</div><input value={model.measurements?.bust ?? ""} onChange={e => setMeas("bust", e.target.value)} placeholder="84" /></label>
          <label><div>Waist</div><input value={model.measurements?.waist ?? ""} onChange={e => setMeas("waist", e.target.value)} placeholder="60" /></label>
          <label><div>Hips</div><input value={model.measurements?.hips ?? ""} onChange={e => setMeas("hips", e.target.value)} placeholder="90" /></label>
          <label><div>Shoe</div><input value={model.measurements?.shoe ?? ""} onChange={e => setMeas("shoe", e.target.value)} placeholder="38 EU" /></label>
          <label><div>Eyes</div><input value={model.measurements?.eyes ?? ""} onChange={e => setMeas("eyes", e.target.value)} placeholder="Brown" /></label>
          <label><div>Hair</div><input value={model.measurements?.hair ?? ""} onChange={e => setMeas("hair", e.target.value)} placeholder="Black" /></label>
        </div>
      </section>

      {/* Hero image */}
      <section style={{ marginTop: 18, padding: 12, border: "1px solid var(--stroke, #2a2f3a)", borderRadius: 12 }}>
        <h3>Hero Image</h3>
        {model.heroUrl ? (
          <div style={{ display: "grid", gap: 10, gridTemplateColumns: "220px 1fr" }}>
            <img src={model.heroUrl} alt="hero" style={{ width: 220, height: 280, objectFit: "cover", borderRadius: 12 }} />
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button onClick={onPickHero}>Replace</button>
              <button onClick={() => setField("heroUrl", "")}>Remove</button>
            </div>
          </div>
        ) : (
          <button onClick={onPickHero}>Upload hero</button>
        )}
        <input
          type="file"
          ref={heroInputRef}
          accept="image/*"
          onChange={(e) => e.target.files?.[0] && uploadHero(e.target.files[0])}
          style={{ display: "none" }}
        />
      </section>

      {/* Gallery */}
      <section style={{ marginTop: 18, padding: 12, border: "1px solid var(--stroke, #2a2f3a)", borderRadius: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>Gallery ({model.images?.length ?? 0})</h3>
          <div>
            <button onClick={onPickGallery}>Add images</button>
            <input
              type="file"
              ref={galleryInputRef}
              accept="image/*"
              multiple
              onChange={(e) => e.target.files && uploadGallery(e.target.files)}
              style={{ display: "none" }}
            />
          </div>
        </div>

        {(!model.images || model.images.length === 0) && <p>No images yet.</p>}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 12, marginTop: 12 }}>
          {model.images?.map((img, idx) => (
            <div key={img.url} style={{ border: "1px solid var(--stroke, #2a2f3a)", borderRadius: 12, padding: 8 }}>
              <img src={img.url} alt="" style={{ width: "100%", height: 180, objectFit: "cover", borderRadius: 8 }} />
              <input
                style={{ width: "100%", marginTop: 6 }}
                placeholder="Caption"
                value={img.caption ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setModel((m) => {
                    const arr = [...m.images];
                    arr[idx] = { ...arr[idx], caption: v };
                    return { ...m, images: arr };
                  });
                }}
              />
              <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                <button onClick={() => moveImage(idx, -1)} disabled={idx === 0}>↑</button>
                <button onClick={() => moveImage(idx, +1)} disabled={idx === (model.images.length - 1)}>↓</button>
                <button onClick={() => removeImage(img)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <footer style={{ marginTop: 24, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button onClick={() => navigate(-1)}>Cancel</button>
        <button disabled={saving} onClick={onSave}>{saving ? "Saving…" : "Save Changes"}</button>
      </footer>
    </div>
  );
};

export default AdminEditModelPage;
